import { COLORS } from 'anchor-server-common/utilities/episode/constants';
import { AD_STATE } from 'anchor-server-common/utilities/ad/constants';
import {
  PODCAST_EPISODE_AUDIO_TYPES,
  getBaseUrl,
  isIOS,
} from '../helpers/serverRenderingUtils';

// TODO (bui): merge these structs
const EDITABLE_AUDIO_TYPES = [
  COLORS.RECORD,
  COLORS.RECORD_WITH_FRIENDS,
  COLORS.CALL_IN,
  COLORS.UPLOAD_FROM_WEB,
  COLORS.DRAFT,
].map(item => item.toLowerCase());

export const MAX_FILE_SIZE = { size: 262144000, label: '250' }; // 250MB

export const isEpisodeAudioEditable = audio =>
  EDITABLE_AUDIO_TYPES.includes(audio.color && audio.color.toLowerCase());

export const getIsAudioAdSlot = audio =>
  typeof audio.isAdSlotEnabled !== 'undefined';

export const getIsAdSlotEnabled = audio => audio.isAdSlotEnabled;

export const PODCAST_COLORS = {
  BLACK: {
    colorClass: 'black',
    color: '#282F36',
    colorLight: '#282F36',
    colorDark: '#282F36',
  },
  GRAY: {
    colorClass: 'gray',
    color: '#8940FA',
    colorLight: '#8F9196',
    colorDark: '#55595E',
  },
  LIGHT_GRAY: {
    colorClass: 'lightgray',
    color: '#CCCDCF',
    colorLight: '#d1d2d3',
    colorDark: '#b7b8ba',
  },
  ORANGE: {
    colorClass: 'orange',
    color: COLORS.RECORD,
    colorLight: '#FED1D1',
    colorDark: '#E14040',
  },
  PURPLE: {
    colorClass: 'purple',
    color: COLORS.DRAFT,
    colorLight: '#DBC5FD',
    colorDark: '#6732B9',
  },
  BLUE: {
    colorClass: 'blue',
    color: COLORS.MUSIC,
    colorLight: '#C6DDFF',
    colorDark: '#1674FF',
  },
  PINK: {
    colorClass: 'pink',
    color: COLORS.INTERLUDE,
    colorLight: '#F6D4F7',
    colorDark: '#E59ACF',
  },
  CYAN: {
    colorClass: 'cyan',
    color: COLORS.CALL_IN,
    colorLight: '#81E2D1',
    colorDark: '#28BAA1',
  },
  WHITE: {
    colorClass: 'white',
    color: '#FFFFFF',
    colorLight: '#FFFFFF',
    colorDark: '#FFFFFF',
  },
  GREEN: {
    colorClass: 'green',
    color: '#1cee4e',
    colorLight: '#8DF6A6',
    colorDark: '#1cee4e',
  },
};

export const editorPaths = {
  dashboardPath: '/dashboard',
  editEpisodePattern: /\/dashboard\/episode\/[^\/]*\/edit\//,
  newEpisodeBasePath: '/dashboard/episode/new',
};

const podcastColorsByColor = Object.keys(PODCAST_COLORS).reduce(
  (_dict, key) => {
    const dict = _dict;
    const color = PODCAST_COLORS[key];
    dict[color.color] = color;
    return dict;
  },
  {}
);

export function getColorsForAudio(audio) {
  return (
    podcastColorsByColor[audio.color] ||
    getColorsForCreationAreaType(audio.type)
  );
}

const TYPES_BY_COLOR = {
  '#2dcfb3': 'callIn',
  '#e14040': 'record',
  '#8940fa': 'library',
  '#fface7': 'interlude',
  '#1cee4e': 'sponsorship',
};

export const getTypeByColor = audio => TYPES_BY_COLOR[audio.color];

export const isBackgroundTrackDisabled = audioType =>
  ['callIn', 'interlude', 'sponsorship'].includes(audioType);

// purple, pink, , secondary color is hex value of color with .3 opacity
// Alternatively return a classname
// TODO (bui): deprecate this and use structs
export function getColorsForCreationAreaType(type) {
  let colors = null;
  switch (type) {
    case 'ad':
      colors = PODCAST_COLORS.GREEN;
      break;
    case 'upload':
      colors = PODCAST_COLORS.BLACK;
      break;
    case 'record':
    case 'mobileRecording':
      colors = PODCAST_COLORS.ORANGE;
      break;
    case 'callins':
    case 'callIn':
      colors = PODCAST_COLORS.CYAN;
      break;
    case 'interludes':
    case 'interlude':
    case 'transitions':
      colors = PODCAST_COLORS.PINK;
      break;
    case 'library':
    default:
      colors = PODCAST_COLORS.PURPLE;
      break;
  }
  return colors;
}

// second representations of time
// todo: get a library for months, leap years, etc.
const YEAR_SECS = 31536000; // not leap year
const MONTH_SECS = 2628000; // rough estimate
const WEEK_SECS = 604800;
const DAY_SECS = 86400;
const HR_SECS = 3600;
const MIN_SECS = 60;

// Return string representation of time elapsed since last date (string)
// Todo: months
export function formatDateSinceCreated(date, isDate = false, short = false) {
  if (!date || (!isDate && typeof date !== 'string')) return null;
  const now = Date.now();
  const difference = (!isDate ? now - new Date(date) : now - date) / 1000;
  if (difference < MIN_SECS) {
    return 'Just now';
  }
  if (difference < HR_SECS) {
    const minutes = Math.round(difference / MIN_SECS);
    return `${minutes} minutes ago`;
  }
  if (difference < DAY_SECS) {
    const hours = Math.round(difference / HR_SECS);
    return `${hours} hours ago`;
  }
  const nowYear = new Date().getFullYear();
  const doHideYear =
    (!isDate ? new Date(date) : date).getFullYear() === nowYear;
  return formatDate(date, 'days', { isShortFormat: short, doHideYear });
  /*
  if (difference < WEEK_SECS) {
    const days = Math.round(difference / DAY_SECS);
    return `${days}d ago`;
  }
  if (difference < MONTH_SECS) {
    const weeks = Math.round(difference / WEEK_SECS);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (difference < YEAR_SECS) {
    const months = Math.round(difference / MONTH_SECS);
    return `${months} ${short ? 'mo' : months > 1 ? 'months' : 'month'} ago`;
  }
  const years = Math.round(difference / YEAR_SECS);
  return `${years} year${years > 1 ? 's' : ''} ago`;
  */
}

export function msToHMS(ms) {
  if (!ms) {
    return null;
  }
  let seconds = ms / 1000;
  let minutes = parseInt(seconds / 60, 10);
  const hours = parseInt(minutes / 60, 10);
  seconds = Math.floor(seconds % 60);
  minutes = Math.floor(minutes % 60);
  const minutesSeconds = minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
  return hours ? `${hours}h ${minutesSeconds}` : minutesSeconds;
}

export function msToDigital(ms) {
  if (ms !== 0 && !ms) {
    return null;
  }
  let seconds = ms / 1000;
  let minutes = parseInt(seconds / 60, 10);
  const hours = parseInt(minutes / 60, 10);
  seconds = Math.floor(seconds % 60);
  minutes = Math.floor(minutes % 60);
  const secondsStr = seconds < 10 ? `0${seconds}` : `${seconds}`;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return hours
    ? `${hours}:${minutesStr}:${secondsStr}`
    : `${minutesStr}:${secondsStr}`;
}

// Temp
// TOOD (bui): build dedicated route for image uploads
// This should also be in a different file
export function uploadImageData(file, options = {}) {
  const formData = new FormData();
  formData.append('file', file);
  for (const k in options) {
    formData.append(k, options[k]);
  }
  return new Promise((resolve, reject) => {
    fetch('/api/onboarding/image', {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    })
      .then(resolve)
      .catch(reject);
  })
    .then(response => {
      if (response.status === 400) {
        return Promise.reject(new Error('Missing image'));
      }
      return response.json();
    })
    .then(responseJson => {
      if (responseJson.image) {
        // receive image as the url
        return Promise.resolve(responseJson);
      }
    })
    .catch(err => {
      console.log(err);
      throw new Error('Unable to upload');
    });
}

// Takes a Javsacript date object and formats it into a date representation
// unit specifies what granularity to represent the date
// Short represents months as the full string
export function formatDate(
  date,
  unit = 'days',
  { doHideYear = false, isShortFormat = false } = {}
) {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  if (unit === 'years') {
    return year;
  }
  const locale = 'en-US';
  const month = isShortFormat
    ? dateObj.getMonth() + 1
    : dateObj.toLocaleString(locale, { month: 'short' });
  if (unit === 'months') {
    if (doHideYear) {
      return month;
    }
    return isShortFormat ? `${month}/${year}` : `${month}, ${year}`;
  }
  const day = dateObj.getDate();
  let dateString = isShortFormat ? `${month}/${day}` : `${month} ${day}`;
  if (!doHideYear) {
    dateString =
      dateString +
      (isShortFormat ? `/${year.toString().slice(2)}` : `, ${year}`);
  }
  if (unit === 'days' || unit === 'weeks') {
    return dateString;
  }
  // unit === 'minutes'
  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  return `${dateString} ${getAmPmHour(hours)}:${
    minutes < 10 ? `0${minutes}` : minutes
  } ${hours < 12 ? 'AM' : 'PM'}`;
}

export function getAmPmHour(dateHour = 0) {
  return dateHour % 12 || 12;
}

// TODO: this should be done using Redux, but we don't currently hold normalized feed data in the redux store.
export function searchSegmentFeedForQuery(
  segmentFeed,
  query = '',
  field = 'caption'
) {
  return new Promise((resolve, reject) => {
    const retArr = [];
    const q = query.toLowerCase();
    for (let i = 0; i < segmentFeed.length; i++) {
      const cur = segmentFeed[i][field];
      if (cur) {
        const searchString = cur.toLowerCase();
        if (searchString.includes(q)) {
          retArr.push(segmentFeed[i]);
        }
      }
    }
    resolve(retArr);
  });
}

export function getChildPathsFromUrl(url) {
  return url.split('/').filter(Boolean);
}

export function getMaxFromArray(arr) {
  if (arr.length === 0) {
    return null;
  }
  return arr.reduce((a, b) => Math.max(a, b));
}

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// TODO: refactor this to just take a `shareLinkPath` and not any object
export function getV3ShareUrl(shareObject = {}, activeAudio) {
  // TODO: clean audio title
  return `${getBaseUrl()}${shareObject.shareLinkPath}${
    activeAudio && activeAudio.stationAudioId ? `/a-${activeAudio.audioId}` : ''
  }`;
}

// TODO: refactor this to just take a `shareLinkEmbedPath` and not any object
export function getV3ShareEmbedHtml(shareObject = {}, activeAudio) {
  const url = `${getBaseUrl()}${shareObject.shareLinkEmbedPath}${
    activeAudio && activeAudio.stationAudioId ? `/a-${activeAudio.audioId}` : ''
  }`;
  return `<iframe src="${url}" height="102px" width="400px" frameborder="0" scrolling="no"></iframe>`;
}

export function shuffleArray(arr) {
  const array = arr.slice();
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * React router v3 would parse the /name-hash/ URL segments properly
 * but v4 does a forward search so it thinks something like this:
 *
 * /hello-there-abc123/
 *
 * Yields a name of "hello" and a hash of "there-abc123"
 * So we do our own regex now
 */
export function getMetadataFromParams({
  episodeHashBeforeHyphen = '',
  episodeHashAfterHyphen = '',
  audioCaptionAndAudioIdHash = '',
}) {
  // re-assemble before parsing (split to differentiate from 2.0 route)
  const episodeNameAndEpisodeIdHash = `${episodeHashBeforeHyphen}-${episodeHashAfterHyphen}`;
  const episodeSplit = splitByLastHyphen(episodeNameAndEpisodeIdHash);
  const audioSplit = audioCaptionAndAudioIdHash
    ? splitByLastHyphen(audioCaptionAndAudioIdHash)
    : [];
  return {
    episodeName: episodeSplit[0],
    episodeIdHash: episodeSplit[1],
    audioCaption: audioSplit[0],
    audioIdHash: audioSplit[1],
  };
}

function splitByLastHyphen(word = '', splitPhrase = '-') {
  const splitWord = word.split(splitPhrase);
  const lastWord = splitWord.pop();
  const firstWord = splitWord.join(splitPhrase);
  return [firstWord, lastWord];
}

// Converts a number into a 'G', 'M', 'k' representation
//   NOTE: the k is only added starting at 100,000. This was
//     a design decision made for the podcast play count graph.
//     If this changes, this function should move to the graph
//     source.
//   Ex:
//     nFormatter(1000) //=> "1.0k"
//     nFormatter(10000) //=> "10.0k"
//     nFormatter(20200) //=> "20.2k"
//     nFormatter(10000000) //=> "1.0M"
export function nFormatter(num) {
  const BILLION = 1000000000;
  const MILLION = 1000000;
  const THOUSAND = 100000;
  if (num >= BILLION) {
    return `${Number(num / BILLION).toFixed(0)}G`;
  }
  if (num >= MILLION) {
    return `${Number(num / MILLION).toFixed(0)}M`;
  }
  if (num >= THOUSAND) {
    return `${Number(num / 1000).toFixed(0)}k`;
  }
  return numberWithCommas(num);
}

export function getSeekAudioIndexFromEpisodeAudiosAndPositionInMs(
  episodeAudios = [],
  positionInMs = 0
) {
  let seekingAudioDuration;
  let seekingDurationPlayed = 0;
  let i;
  for (i = 0; i < episodeAudios.length; i++) {
    seekingAudioDuration = episodeAudios[i].duration;
    if (positionInMs < seekingAudioDuration + seekingDurationPlayed) {
      return {
        audioIndex: i,
        audioPositionInMs: positionInMs - seekingDurationPlayed,
      };
    }
    seekingDurationPlayed = seekingDurationPlayed + seekingAudioDuration;
  }
  return {
    audioIndex: i,
    audioPositionInMs: positionInMs - seekingDurationPlayed,
  };
}

export function copyTextToClipboard(text, el = document.body) {
  const textArea = document.createElement('textarea');
  const range = document.createRange();
  let s;

  textArea.style.position = 'fixed';
  textArea.style.top = 0;
  textArea.style.left = 0;

  textArea.style.width = '2em';
  textArea.style.height = '2em';
  textArea.style.fontSize = '16px'; // prevent iOS zooming

  textArea.style.padding = 0;

  textArea.style.border = 'none';
  textArea.style.outline = 'none';
  textArea.style.boxShadow = 'none';

  textArea.style.background = 'transparent';

  textArea.contentEditable = true;
  textArea.readOnly = false;

  textArea.value = text;
  el.appendChild(textArea);
  if (isIOS()) {
    range.selectNodeContents(textArea);
    s = window.getSelection();
    s.removeAllRanges();
    s.addRange(range);
    textArea.setSelectionRange(0, 999999);
  } else {
    textArea.select();
  }

  try {
    const successful = document.execCommand('copy');
    const msg = successful ? 'successful' : 'unsuccessful';
  } catch (err) {
    console.error('Unable to copy');
  }

  el.removeChild(textArea);
}

export const getIsShowingFirstTimeActivateSponsorships = ({
  isImportedPodcast,
  isImportedPodcastRedirected,
  podcastEpisodeId,
  doHideDefaultAdSlotsInEpisodes,
  isAllowedToAuthenticateStripe,
  hasEverEnabledSponsorships,
}) => {
  const isNotImportedOrRedirectedPodcast = isImportedPodcast
    ? isImportedPodcastRedirected
    : true;
  const isASavedPodcast =
    podcastEpisodeId !== null || podcastEpisodeId !== undefined;
  const hasNotEngagedWithAdSlotButton =
    doHideDefaultAdSlotsInEpisodes === undefined &&
    !doHideDefaultAdSlotsInEpisodes;
  const doesNotHaveAdSlotsEnabledOnAnyEpisode =
    hasEverEnabledSponsorships === false;
  return (
    isNotImportedOrRedirectedPodcast &&
    isASavedPodcast &&
    hasNotEngagedWithAdSlotButton &&
    isAllowedToAuthenticateStripe &&
    doesNotHaveAdSlotsEnabledOnAnyEpisode
  );
};

export const getSecondsInTimeInterval = timeInterval => {
  switch (timeInterval) {
    case 'hour':
      return HR_SECS;
    case 'day':
      return DAY_SECS;
    case 'week':
      return WEEK_SECS;
    case 'month':
      return MONTH_SECS;
    case 'year':
      return YEAR_SECS;
    default:
      throw new Error(`Invalid timeInterval: ${timeInterval}`);
  }
};

export const titleCase = ({ str, removeSpace = false }) =>
  str
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (!word) return '';
      return word.replace(word[0], word[0].toUpperCase());
    })
    .join(removeSpace ? '' : ' ');

const wait = ms => new Promise(r => setTimeout(r, ms));

export const retryOperation = (operation, delay, times) =>
  new Promise((resolve, reject) =>
    operation()
      .then(resolve)
      .catch(reason => {
        if (times - 1 > 0) {
          return wait(delay)
            .then(retryOperation.bind(null, operation, delay, times - 1))
            .then(resolve)
            .catch(reject);
        }
        return reject(reason);
      })
  );

export const getOriginalAudio = ({
  audioSegments,
  audioWebId,
  originalAudioWebId,
}) => {
  if (originalAudioWebId === audioWebId) {
    return null;
  }
  return { ...audioSegments[originalAudioWebId], audioId: originalAudioWebId };
};
